<template>
  <div
    no-body
    class="degree-search-card card"
    :class="cardClass"
    :style="cardStyle"
    @click="handleCardClick">
    <img
      :data-src="imageSource"
      :alt="imageAlt"
      ref="cardImage"
      class="degree-search-card-image d-none d-md-block cds-lazy" />

    <div class="degree-search-card-body card-body">
      <!-- degree label -->
      <p
        v-if="showLabel"
        class="degree-label d-inline-block mt-space-xs mx-space-xs mb-0 font-weight-bold"
        :class="labelClass">
        {{ label }}
      </p>

      <!-- title -->
      <div class="mt-space-xs mt-md-space-sm mt-lg-space-sm">
        <h3 class="mx-space-xs mb-0 font-weight-bold" :class="titleClass">
          {{ title }}
        </h3>
        <hr
          class="title-underline d-inline-block mt-space-xxs mx-space-xs mb-space-sm mb-md-space-sm mb-lg-space-xs"
          :style="underlineStyle" />
      </div>

      <!-- description slot -->
      <div class="mx-space-xs mb-space-sm d-none d-lg-block">
        <!-- slot -->
        <slot />
      </div>
    </div>

    <!-- footer -->
    <div
      v-if="showFooter"
      :class="cardFooterClass"
      class="my-0 py-space-xxs px-0"
      :footer-border-variant="borderVariant">
      <div
        class="d-flex align-items-center justify-content-between mx-space-xs">
        <div>
          <!-- footer label -->
          <p
            v-if="props.footerLabel"
            class="mb-space-xxs"
            :class="footerLabelClass">
            {{ footerLabel }}
          </p>

          <!-- footer text as cta -->
          <a
            v-if="showCta"
            :aria-label="title + '-' + footerCtaText"
            class="mb-0"
            :href="footerCtaLink"
            :class="footerCtaClass"
            @click="handleCtaClick"
            >{{ footerCtaText }}</a
          >

          <!-- footer text as plain text -->
          <div
            v-else
            class="mb-0"
            :class="footerTextClass"
            v-html="footerText"></div>
        </div>

        <!-- footer icon -->
        <div class="">
          <a
            v-if="footerCtaLink"
            :href="footerCtaLink"
            :aria-label="title + '-' + footerCtaText"
            class="text-dark-3"
            @click="handleIconClick">
            <div class="icon-block icon-hover">
              <font-awesome-icon
                :icon="chevronRightIcon"
                :aria-label="title + '-' + footerCtaText"
                class="cta-icon-class"></font-awesome-icon>
            </div>
          </a>
          <div v-else class="icon-block">
            <font-awesome-icon
              :icon="chevronRightIcon"
              role="presentation"
              class="cta-icon-class"></font-awesome-icon>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import lozad from "lozad";
import { computed, onMounted, ref } from "vue";

interface Props {
  showLabel?: boolean;
  label?: string;
  labelSize?: string;
  labelBackground?: string;
  labelVariant?: string;
  underlineColor?: string;
  title: string;
  titleSize?: string;
  titleVariant?: string;
  footerLabel?: string;
  footerLabelSize?: string;
  footerLabelVariant?: string;
  footerText?: string;
  footerTextSize?: string;
  borderVariant?: string;
  bgVariant?: string;
  imageSource: string;
  imageAlt: string;
  highlightColor?: string;
  showFooter?: boolean;
  showCta?: boolean;
  footerCtaText?: string;
  footerCtaLink?: string;
  footerCtaSize?: string;
  footerCtaVariant?: string;
}

const props = withDefaults(defineProps<Props>(), {
  showLabel: true,
  label: "Lorem ipsum",
  labelSize: "xs",
  labelBackground: "dark-3",
  labelVariant: "white",
  underlineColor: "var( --rds-secondary, #ffc627)",
  titleSize: "medium",
  titleVariant: "dark-3",
  footerLabel: "Footer Label",
  footerLabelSize: "small",
  footerLabelVariant: "dark-3",
  footerText: "Footer Text",
  footerTextSize: "medium",
  borderVariant: "light-4",
  bgVariant: "white",
  highlightColor: "var( --rds-secondary, #ffc627)",
  showFooter: true,
  showCta: true,
  footerCtaText: "Learn More",
  footerCtaSize: "medium",
  footerCtaVariant: "primary",
});

// events
const emits = defineEmits<{
  /**
   *  Emitted when any part of the card is clicked except for cta click.
   */
  (e: "cardClick", event: Event): void;
  /**
   *  Emitted when the cta is clicked, if present.
   */
  (e: "ctaClick", event: Event): void;
  /**
   *  Emitted when the icon is clicked.
   */
  (e: "iconClick", event: Event): void;
}>();

const chevronRightIcon = faChevronRight;
const cardImage = ref<HTMLImageElement>();

const getColor = (color: string) => {
  if (color.includes("var(")) {
    return `${color}`;
  } else if (color.includes("--")) {
    return `var(${color})`;
  } else {
    return color;
  }
};

const cardClass = computed(() => {
  return `border border-${props.borderVariant} bg-${props.bgVariant}`;
});

const cardFooterClass = computed(() => {
  return `bg-${props.bgVariant} border-top border-${props.borderVariant}`;
});

const cardStyle = computed(() => {
  return {
    "--highlight-color": getColor(props.highlightColor),
    "border-bottom": `7px solid ${getColor(props.highlightColor)} !important`,
  };
});

const labelClass = computed(() => {
  return `fs-${props.labelSize} text-${props.labelVariant} bg-${props.labelBackground}`;
});

const footerLabelClass = computed(() => {
  return `fs-${props.footerLabelSize} text-${props.footerLabelVariant}`;
});

const footerTextClass = computed(() => {
  return `fs-${props.footerTextSize}`;
});

const footerCtaClass = computed(() => {
  return `fs-${props.footerCtaSize} text-${props.footerCtaVariant}`;
});

const underlineStyle = computed(() => {
  return {
    "background-color": getColor(props.underlineColor),
  };
});

const titleClass = computed(() => {
  return `h3-${props.titleSize} text-${props.titleVariant}`;
});

const handleCardClick = (event: Event) => {
  emits("cardClick", event);
};

const handleCtaClick = (event: Event) => {
  event.stopPropagation();
  emits("ctaClick", event);
};

const handleIconClick = (event: Event) => {
  event.stopPropagation();
  emits("iconClick", event);
};

onMounted(() => {
  if (typeof window !== "undefined" && window !== null) {
    const observer = lozad(cardImage.value);
    observer.observe();
  }
});
</script>

<style lang="scss" scoped>
.icon-hover:hover {
  background: var(--highlight-color, #ffc627);
  border: none;
}
.degree-search-card {
  min-height: 190px;
}
.degree-search-card-body {
  padding: 0;
  position: relative;
}
.degree-search-card-image {
  border-radius: 0;
  height: 180px;
  width: 100%;
  object-fit: cover;
  object-position: top;
}
.degree-label {
  font-weight: bold;
  padding: 4px;
}
.title-underline {
  height: 2px;
  width: 12%;
  border-top: none;
}
.icon-block {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 1px solid var(--rds-light-3);
  background: var(--rds-light-1);
  display: flex;
  align-items: center;
}
.cta-icon-class {
  height: 12px;
  width: 100%;
  color: var(--rds-dark-3);
}

/* Medium devices (tablets, 768px and up) */
@media (min-width: 768px) {
  .degree-label {
    top: -30px;
    position: absolute;
  }
}
</style>
